import React, { useState } from 'react';
import { isObject } from 'underscore';

import { UpdateLeaderboardPresets } from '@/connection/leaderboard';
import { updateProjectSettings } from '@/store/slices/project';
import { useAppSelector, useAppDispatch } from '@/store/hooks';
import { getActiveChannel } from '@/store/slices/user/selectors';
import { EventSettings } from '@/types/projects.types';
import {
  GetUploadEndpoint,
  getUploadFilename,
  UploadFileToProvider,
  SetItemContentType,
} from '@ui/uploads/validations';
import toastTrigger from '@ui/toast/toast-trigger';
import UploadLarge from '@ui/uploads/upload-large';
import { ICONS } from '@ui/icons/types';
import Icon from '@ui/icons/icon';
import Modal from '@ui/modal/modal';
import WaitingIndicator from '@ui/waiting-indicator/waiting-indicator';
import { LeaderboardSettings } from './types';
import { getIsSettingsUpdating, getWorkingProject } from '@/store/slices/project/selectors';
import Container from '@ui/container/container';
import Button from '@ui/buttons/button/button';
import Text from '@ui/text/text/text';
import getThemeColorCSSVariable from '@/utils/colors/get-theme-color-css-variable';
import getThemeColorContrastCSSVariable from '@/utils/colors/get-theme-color-contrast-css-variable';
import { EColorVariablesV2 } from '@/types/theme-packs.types';

interface LeaderboardCsvModalProps {
  open: boolean;
  close: () => void;
  submit: () => void;
  currentSettings?: LeaderboardSettings;
}

const LeaderboardCsvModal: React.FC<LeaderboardCsvModalProps> = ({ open, close, submit, currentSettings }) => {
  const active_channel = useAppSelector(getActiveChannel);
  const workingEvent = useAppSelector(getWorkingProject);
  const isUpdating = useAppSelector(getIsSettingsUpdating);

  const hasDownload = !!currentSettings?.upload_url && !!currentSettings?.upload_url;

  const dispatch = useAppDispatch();

  const [loading, setLoading] = useState(false);
  const [fileName, setFileName] = useState('');
  const [url, setUrl] = useState('');
  const [hasFile, setHasFile] = useState(false);
  const [filesize, setFilesize] = useState(0);

  async function onFile(file: File | FileList) {
    if (file instanceof File && workingEvent) {
      setLoading(true);
      setFileName(file.name);
      try {
        const endpoint = await GetUploadEndpoint({
          filename: getUploadFilename(file.name),
        });

        if (isObject(endpoint) && endpoint.url) {
          const finalUrl = await UploadFileToProvider(
            {
              url: endpoint.url,
              fields: endpoint.fields,
              cdnHost: endpoint?.cdnHost,
            },
            file
          );
          SetItemContentType({ filename: finalUrl, contentType: file.type }).catch(console.error);

          setUrl(finalUrl);
          setFilesize(file.size);
        }
      } catch (e) {
        console.error(e);
        toastTrigger(
          <div>
            <h6>Error attaching file</h6>
            <p>There was an error attaching the file.</p>
          </div>,
          { type: 'error' }
        );
      }
      setLoading(false);
      setHasFile(true);
    }
  }

  async function save() {
    if (hasFile && workingEvent && active_channel) {
      try {
        const eventSettings: EventSettings = {
          ...workingEvent.settings,
          leaderboard_settings: {
            ...workingEvent.settings.leaderboard_settings,
            upload_url: url,
            upload_filename: fileName,
            upload_filesize: filesize,
          },
        };
        dispatch(updateProjectSettings({ event_uuid: workingEvent.uuid, settings: eventSettings }));
        await UpdateLeaderboardPresets(active_channel, url, workingEvent.event);
      } catch (e) {
        console.error(e);
        toastTrigger(
          <div>
            <h6>Error saving file</h6>
            <p>There was an error saving the file.</p>
          </div>,
          { type: 'error' }
        );
      }
    }
    submit();
    close();
  }

  const downloadCurrent = async () => {
    if (currentSettings?.upload_filename && currentSettings.upload_url) {
      try {
        const link = document.createElement('a');
        link.setAttribute('href', currentSettings.upload_url);
        link.setAttribute('download', currentSettings.upload_filename);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (e) {
        console.error(e);
        toastTrigger(
          <div>
            <h6>Error downloading</h6>
            <p>There was an error downloading the current list.</p>
          </div>,
          { type: 'error' }
        );
      }
    }
  };

  const downloadTemplate = () => {
    let csv = 'email,action,points,track\n';
    csv += 'user1@example.com,custom-action,10,gold';
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const link = document.createElement('a');
    link.setAttribute('href', URL.createObjectURL(blob));
    link.setAttribute('download', 'custom-actions-example.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Modal
      open={open}
      onRequestClose={close}
      customSize={{
        width: '640px',
        height: '400px',
        minHeight: '100px',
        maxHeight: 'calc(100% - 32px)',
        maxWidth: 'calc(100% - 32px)',
      }}
      RenderFooterContent={() => (
        <Container
          className="flex fd-row jc-flex-end ai-center gap-8"
          padding="12px 40px"
          background="var(--gray-900)"
          borderRadius="0 0 12px 12px"
        >
          <Button onClick={close} focusBackgroundColor="var(--gray-700)">
            <Text>Cancel</Text>
          </Button>
          <Button
            width="94px"
            height="32px"
            onClick={save}
            disabled={isUpdating}
            className="flex ai-center jc-center"
            backgroundColor={getThemeColorCSSVariable()}
          >
            <Text
              className="L1"
              color={getThemeColorContrastCSSVariable(EColorVariablesV2.buttonText, EColorVariablesV2.primary)}
            >
              {isUpdating ? <WaitingIndicator /> : 'Save'}
            </Text>
          </Button>
        </Container>
      )}
      modalClassName="overflow-hidden"
    >
      <Container background="var(--gray-900)" padding="24px 40px" height="100%">
        <div className="leaderboard-csv-modal-wrapper">
          <div className="leaderboard-csv-modal-container">
            <div className="leaderboard-csv-modal-header">
              <Text tag="h4">Leaderboard</Text>
              {hasDownload && (
                <Button className="flex ai-center" onClick={downloadCurrent} focusBackgroundColor="var(--gray-700)">
                  <Icon name={ICONS.DOWNLOAD} size={12} color="#FFF" />
                  <Text tag="span">Current version</Text>
                </Button>
              )}
            </div>
            <Text tag="label" className="header-label">
              Please use the CSV template to properly format your custom points for upload
            </Text>

            <UploadLarge
              onFile={onFile}
              title="Upload file"
              subtitle="or drag and drop .csv file here"
              allowedFileTypes={['text/csv']}
              style={{ height: '100px' }}
              uploading={loading}
            />
            <div className="leaderboard-csv-upload-info">
              <Text tag="label">
                If you're planning to generate custom points, please download this template to correctly format the
                document
              </Text>
              <Button
                onClick={downloadTemplate}
                backgroundColor="var(--gray-1000)"
                focusBackgroundColor="var(--gray-700)"
              >
                <Icon name={ICONS.DOWNLOAD} size={12} color="#FFF" />
                <Text tag="span">Download template</Text>
              </Button>
            </div>
          </div>
        </div>
      </Container>
    </Modal>
  );
};

export default LeaderboardCsvModal;
